import React, { useEffect, useRef } from "react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import * as Yup from "yup";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import { makeStyles } from "@mui/styles";

import CustomButton from "components/CustomButtons";
import { useLoading, useSnackbar } from "contexts";
import useEditorStore from "editors/TemplateEditor/editorStore";
import { getBatches, getDrafts, getTemplates, saveDraft } from 'services';
import { Session } from "utils";
import { useServices } from "contexts/SingleSessionProvider";
import TextField from "components/CustomFields";
import { SimpleSelect } from "components/CustomSelectFields";
import ProctorSettings from "./MockProctorSettings";
import MockDatePicker from "./MockDatePicker";

dayjs.extend(advancedFormat);

const ValidationSchema = Yup.object({
  name: Yup.string().trim().required("Give a name to your template"),
  batch: Yup.string().required("Select a batch"),
  publishOn: Yup.date().required(),
  expiresOn: Yup.date().required(),
  proctorSettings: Yup.object({
    copyPasteProctoring: Yup.bool().optional().default(true),
    generalProctoring: Yup.bool().optional().default(false),
    imageCaptureProctoring: Yup.bool().optional().default(false),
  }).optional(),
});

const DefaultValues = {
  name: "",
  batch: "",
  publishOn: "",
  expiresOn: "",
  proctorSettings: {
    copyPasteProctoring: true,
    generalProctoring: true,
    imageCaptureProctoring: false,
  },
};

const useStyles = makeStyles((theme) => ({
  borderBottom: {
    borderBottom: "1px solid #CCD4DE",
  },
  padding: {
    padding: 0
  },
  box: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4)
  }
}))

const ActionStyle = {
  width: "100%",
  margin: 0,
};


function DetailsForm({ sendValue,setSendValue,setRefresh, onClose, ...props }) {
  const classes = useStyles();
  const loading = useLoading();
  const snackbar = useSnackbar();
  const service = useServices();
  const hasSetNameRef = useRef(false);

  const draftId = useEditorStore((state) => state.draftId);
  const reset = useEditorStore((state) => state.reset);

  const [options, setOptions] = React.useState([]);
  const [selectedBatch, setSelectedBatch] = React.useState();

  React.useEffect(() => {
    getBatches({ client: Session.userId })
      .then(({ batches }) => setOptions(batches))
      .catch(console.error);
  }, []);

  const fields = new Set([
    "_id",
    "name",
    "expiresOn",
    "publishOn",
    "batch",
    "proctorSettings",
    "company",
    "jobRole",
    "jobDescription",
    "domain",
    "type"
  ]);

  const {
    handleSubmit,
    values,
    handleChange,
    errors,
    setFieldValue,
    handleBlur,
    touched,
  } = useFormik({
    initialValues: DefaultValues,
    validationSchema: ValidationSchema,
    onSubmit: async () => {
      try {
        loading.show();
        const filteredValues = Object.keys(values).reduce((acc, key) => {
          if (fields.has(key) && values[key] !== undefined) {
              acc[key] = values[key];
          }
          return acc;
        }, {});

        Object.keys(sendValue).forEach((key) => {
          if (fields.has(key) && sendValue[key] !== undefined) {
              filteredValues[key] = sendValue[key];
          }
        });
        
        await saveDraft(draftId,{name:"INTERVIEW_DETAILS",data: {...filteredValues,type:"personalized"}});
        setSendValue();
        reset();
        props.setnextStep(0);
        setRefresh(true)
        snackbar.showSnackbar("Published Successfully");
        onClose();
      } catch (error) {
        snackbar.showSnackbar(
          error.message || "Uh Oh! Unable to save your changes",
          "error"
        );
      } finally {
        loading.hide();
      }
    },
  });

  const handleDateChange = React.useCallback(
    (name, date) => setFieldValue(name, date),
    [setFieldValue]
  );

  const isPublishDisabled = !values.name || !values.batch || !values.publishOn || !values.expiresOn;

  useEffect(() => {
    (async () => {
      if (!hasSetNameRef.current) {
        try {
          let name = `${sendValue?.company}: ${sendValue?.jobRole}`;
          
          const filter = { name: name, isActive: true };
          const draftFilters = { user: Session.userId, type: "personalized" };
          const { interviewTemplates } = await service.callService(getTemplates, filter);
          const { drafts } = await service.callService(getDrafts, draftFilters);

          const filteredDrafts = drafts.filter(draft => draft.user?.client === Session.userId);
  
          let newInterviewName = name;
  
          if (interviewTemplates.length > 0) {
            const suffix = (interviewTemplates.length).toString().padStart(2, '0');
            newInterviewName = `${name}-${suffix}`;
          }
  
          const checkNameConflict = (nameToCheck) => {
            let increment = 1;
  
            const hasConflict = (nameToCheck) => {
              const matchingTemplate = interviewTemplates.find(template => template.name === nameToCheck);
              const matchingDraft = filteredDrafts.find(draft => draft.data.INTERVIEW_DETAILS?.name === nameToCheck);
              return matchingTemplate || matchingDraft;
            };
  
            let nameWithIncrement = nameToCheck;
            while (hasConflict(nameWithIncrement)) {
              const suffix = increment.toString().padStart(2, '0'); 
              nameWithIncrement = `${nameToCheck}-${suffix}`;
              increment++;
            }
  
            return nameWithIncrement;
          };
  
          newInterviewName = checkNameConflict(newInterviewName);
  
          setFieldValue('name', newInterviewName);
  
          hasSetNameRef.current = true;
        } catch (error) {
          console.error(error);
        }
      }
    })();
  }, []);

  return (
    <Box {...props}>
      <form className={classes.box}>
        <TextField
          label="Next Round Mock name"
          name="name"
          placeholder="Select"
          value={values?.name}
          onChange={handleChange}
          sx={{width: '100%'}}
          error={touched.name && errors.name}
        />
        <SimpleSelect
          label="Publishing in Batch"
          name="batch"
          options={options}
          value={values.batch}
          sx={{width: '100%'}}
          onSelect={handleChange}
          error={touched.batch && errors.batch}
        />
        <MockDatePicker
          name="publishOn"
          label="Publish mock interview"
          value={values.publishOn}
          onChange={handleDateChange}
          defaultValue={dayjs().toISOString()}
          width="unset"
        />
        <MockDatePicker
          name="expiresOn"
          label="Expiry mock interview"
          value={values.expiresOn}
          onChange={handleDateChange}
          disabled={!!!values.publishOn}
          minDate={values?.publishOn ? values.publishOn : undefined}
          defaultValue={dayjs().set("year", 9999).endOf("year").toISOString()}
          width="unset"
        />
        <ProctorSettings
          values={values?.proctorSettings || {}}
          onChange={handleChange}
          boxShadow='none'
        />
        <CustomButton
          variant="contained"
          onClick={handleSubmit}
          style={ActionStyle}
          disabled={isPublishDisabled}
        >
          Publish Now
        </CustomButton>
      </form>
    </Box>
  );
}

export default DetailsForm;
